import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { X, Trash, Plus, Minus, ShoppingCart } from 'phosphor-react';

// Cart item component for mobile modal
function MobileCartItem({ item, onUpdateQuantity, onRemove, t, showToast, toastMessage }) {
  return (
    <div className="flex items-center space-x-4 p-4 border-b border-gray-100 last:border-b-0">
      <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
          <span className="text-xs font-medium text-ambre-600">Card</span>
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{item.name}</h4>
        <p className="text-xs text-gray-500">{t('product.quantity')}: {item.quantity}</p>
        <div className="text-sm font-medium text-ambre-600">
          €{(item.price * item.quantity).toFixed(2)}
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
        >
          <Minus size={16} className="text-gray-600" />
        </button>
        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
        >
          <Plus size={16} className="text-gray-600" />
        </button>
        <button
          onClick={() => onRemove(item.id)}
          className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition ml-2"
        >
          <Trash size={16} className="text-red-600" />
        </button>
      </div>
      
      {showToast && (
        <div className="absolute top-0 left-0 right-0 bg-red-100 text-red-800 text-xs p-2 text-center">
          {toastMessage}
        </div>
      )}
    </div>
  );
}

// Discount item component for mobile modal
function MobileCartDiscountItem({ discount, t }) {
  return (
    <div className="flex items-center space-x-4 p-4 bg-green-50 border-b border-green-100 last:border-b-0">
      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-green-600 text-sm font-bold">%</span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-green-800 truncate">{discount.name}</h4>
        <p className="text-xs text-green-600">Korting toegepast</p>
      </div>
      <div className="text-sm font-medium text-green-600">
        €{discount.price.toFixed(2)}
      </div>
    </div>
  );
}

function MobileCartModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { 
    items, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal, 
    getCartSubtotal, 
    getCartShipping, 
    getDiscountItems, 
    getCartItemsCount 
  } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastItemId, setToastItemId] = useState(null);

  // Handle quantity update with stock validation and user feedback
  const handleQuantityUpdate = async (productId, newQuantity) => {
    const item = items.find(item => item.id === productId);
    if (!item) return;

    // Check stock limit
    if (item.stock_quantity !== undefined && item.stock_quantity > 0) {
      if (newQuantity > item.stock_quantity) {
        setToastMessage(`Maximum ${item.stock_quantity} beschikbaar`);
        setToastItemId(productId);
        setShowToast(true);
        
        // Hide toast after 5 seconds
        setTimeout(() => {
          setShowToast(false);
          setToastItemId(null);
        }, 5000);
        return;
      }
    }

    updateQuantity(productId, newQuantity);
  };

  const handleProceedToCheckout = () => {
    if (items.length === 0) {
      setError(t('cart.empty'));
      return;
    }
    setError('');
    onClose(); // Close the modal first
    navigate('/cart'); // Then navigate to cart page
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div 
        className="fixed top-32 left-0 right-0 bottom-0 bg-white flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with close button */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('cart.title')} ({getCartItemsCount()} {t('common.items')})
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

      {/* Cart Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-4 py-8">
            <ShoppingCart size={64} className="text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg mb-4">{t('cart.empty')}</p>
            <button
              onClick={() => {
                onClose();
                navigate('/shop');
              }}
              className="bg-ambre-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ambre-700 transition"
            >
              {t('shop.viewDetails')}
            </button>
          </div>
        ) : (
          <div className="pb-20">
            {/* Cart Items */}
            <div className="bg-white">
              {items.map((item) => (
                <MobileCartItem
                  key={item.id}
                  item={item}
                  onUpdateQuantity={handleQuantityUpdate}
                  onRemove={removeFromCart}
                  t={t}
                  showToast={showToast && toastItemId === item.id}
                  toastMessage={toastMessage}
                />
              ))}
              
              {/* Discount Items */}
              {getDiscountItems().map((discount) => (
                <MobileCartDiscountItem key={discount.id} discount={discount} t={t} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Summary */}
      {items.length > 0 && (
        <div className="flex-shrink-0 bg-white border-t border-gray-200 p-4">
          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('cart.subtotal')}:</span>
              <span>€{getCartSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('cart.shipping')}:</span>
              <span>
                {getCartShipping() === 0 ? 'Gratis' : `€${getCartShipping().toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between text-lg font-semibold text-gray-900 border-t border-gray-200 pt-2">
              <span>{t('cart.total')}:</span>
              <span className="text-ambre-600">€{getCartTotal().toFixed(2)}</span>
            </div>
          </div>
          
          <button
            onClick={handleProceedToCheckout}
            className="w-full bg-ambre-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-ambre-700 transition"
          >
            {t('cart.checkout')}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

export default MobileCartModal;
